import { inject, Injectable } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { combineLatest, fromEvent, Observable, of, timer } from "rxjs";
import { EmotionAngularEvents } from "@services/emotion-events.service";
import { catchError, filter, map, startWith, switchMap } from "rxjs/operators";
import { Store } from "@ngrx/store";
import { AppState, selectFullClient } from "features";
import { FullClient, FullClientType } from "@models/full-client.model";
import { MessageCountApiService } from "./message-count-api.service";

@Injectable({
	providedIn: "root",
})
export class MessageCountFacadeService {
	private readonly store = inject(Store<AppState>);
	private readonly api = inject(MessageCountApiService);

	private readonly MESSAGE_COUNT_DELAY = 60000;
	private readonly fullClient$ = this.store.select(selectFullClient);
	private readonly refreshMessageCount$ = fromEvent(window, EmotionAngularEvents.RELOAD_MESSAGES_COUNT);
	private readonly messageCount$ = combineLatest([
		this.fullClient$.pipe(filter(Boolean)),
		this.refreshMessageCount$.pipe(startWith(void 0)),
		timer(0, this.MESSAGE_COUNT_DELAY),
	]).pipe(switchMap(([fullClient]) => this.loadMessageCount(fullClient)));

	readonly $messageCount = toSignal(this.messageCount$);

	private loadMessageCount(fullClient: FullClient): Observable<number | null> {
		return this.api
			.getCountV2(
				fullClient.type === FullClientType.REMPLACANT
					? fullClient.idSituationRemplacant
					: fullClient.idSituation,
			)
			.pipe(
				catchError(() => {
					const obs =
						fullClient.type === FullClientType.REMPLACANT
							? this.api.getCountRemplacant(fullClient.idSituationRemplacant)
							: this.api.getCountPs(fullClient.idSituation);

					return obs.pipe(
						map((count) => Math.max(parseInt(count, 10), 0)),
						catchError(() => of(null)),
					);
				}),
			);
	}
}
